import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessagesSquare, Phone, X } from "lucide-react";
import { Link } from "react-router-dom";
import { NAV_LINKS, PHONE_DISPLAY, PHONE_TEL } from "../data/site";

const MotionLink = motion(Link);

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 28);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <>
            <header
                data-testid="navbar"
                className={`fixed inset-x-0 top-0 z-50 transition-[padding,background-color,box-shadow,border-color] duration-500 ${
                    scrolled
                        ? "border-b border-navy/5 bg-cream/85 py-2.5 shadow-[0_8px_30px_-12px_rgba(23,59,77,0.18)] backdrop-blur-xl"
                        : "border-b border-transparent bg-cream/40 py-4 backdrop-blur-md"
                }`}
            >
                <div className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="group flex items-center gap-2.5"
                    >
                        <img
                        src="/Images/Logo_Transparent.png"
                        alt="Speech Horizon"
                        className="h-20 w-auto object-contain"
                        />
                    </Link>

                    <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
                        {NAV_LINKS.map((link) => (
                            <Link
                                key={link.path}
                                to={link.path}
                                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="group relative rounded-full px-3.5 py-2 text-[13.5px] font-semibold text-ink/75 transition-colors duration-300 hover:text-teal-dark"
                            >
                                {link.label}
                                <span className="absolute inset-x-3.5 -bottom-0.5 h-0.5 origin-left scale-x-0 rounded-full bg-peach transition-transform duration-300 group-hover:scale-x-100" />
                            </Link>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <Link
                            to="/contact"
                            data-testid="nav-book-consultation-btn"
                            className="hidden rounded-full bg-peach px-5 py-2.5 font-jakarta text-[13px] font-bold text-navy shadow-lg shadow-peach/40 transition-all duration-300 hover:-translate-y-0.5 hover:bg-peach-deep hover:text-cream hover:shadow-xl hover:shadow-peach/40 sm:inline-flex"
                        >
                            Book a Consultation
                        </Link>
                        <button
                            type="button"
                            aria-label="Open menu"
                            data-testid="nav-mobile-menu-btn"
                            onClick={() => setOpen(true)}
                            className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/10 bg-white/70 text-navy lg:hidden"
                        >
                            <Menu size={20} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[70] flex flex-col bg-cream lg:hidden"
                        data-testid="nav-mobile-menu"
                    >
                        <div className="flex items-center justify-between px-5 py-4">
                            <span className="font-jakarta text-lg font-extrabold text-navy">
                                Speech <span className="text-teal">Horizon</span>
                            </span>
                            <button
                                type="button"
                                aria-label="Close menu"
                                data-testid="nav-mobile-close-btn"
                                onClick={() => setOpen(false)}
                                className="flex h-10 w-10 items-center justify-center rounded-full border border-navy/10 bg-white text-navy"
                            >
                                <X size={20} />
                            </button>
                        </div>
                        <nav className="flex flex-1 flex-col justify-center gap-1 px-8" aria-label="Mobile">
                            {NAV_LINKS.map((link, i) => (
                                <MotionLink
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setOpen(false)}
                                    data-testid={`nav-mobile-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    initial={{ opacity: 0, x: -24 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.08 + i * 0.06, duration: 0.45 }}
                                    className="group flex items-center justify-between border-b border-navy/8 py-4 font-jakarta text-2xl font-bold text-navy transition-colors hover:text-teal"
                                >
                                    {link.label}
                                    <span className="text-sm font-medium text-teal opacity-0 transition-opacity group-hover:opacity-100">
                                        →
                                    </span>
                                </MotionLink>
                            ))}
                        </nav>
                        <div className="space-y-3 px-8 pb-10">
                            <Link
                                to="/contact"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-book-btn"
                                className="block rounded-full bg-peach py-4 text-center font-jakarta text-sm font-bold text-navy shadow-lg shadow-peach/30"
                            >
                                Book a Consultation
                            </Link>
                            <a
                                href={PHONE_TEL}
                                data-testid="nav-mobile-call-link"
                                className="flex items-center justify-center gap-2 text-sm font-semibold text-teal-dark"
                            >
                                <Phone size={15} /> {PHONE_DISPLAY}
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};
